import React from 'react';

import { useNavigate } from 'react-router';

import { useTranslation } from 'react-i18next';

import useSystemPlans from '@/hooks/useSystemPlans';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { itemByLanguage } from '@/utils/language';

import PlanCard from '@/components/Cards/PlanCard//PlanCard';
import PlanCardDate from '@/components/Cards/PlanCard//PlanCardComponents/PlanCardDate';
import PlanCardLabel from '@/components/Cards/PlanCard//PlanCardComponents/PlanCardLabel';
import PlanCardSubHeader from '@/components/Cards/PlanCard//PlanCardComponents/PlanCardSubHeader';
import PlanCardTitle from '@/components/Cards/PlanCard//PlanCardComponents/PlanCardTitle';
import PlanCardContent from '@/components/Cards/PlanCard//PlanCardContent/PlanCardContent';

import { routes } from '@/constants';

import PlanCardMenu from './PlanCardMenu';

import { Plan } from '@/_generated/api-clients/xp';

interface ManagedPlansCardProps {
  assignedUsers: number;
  assignPlan: () => void;
  completedUsers: number;
  deletePlan: () => void;
  duplicatePlan: () => void;
  editTranslations: () => void;
  plan: Plan;
  removePlan?: () => void;
  renamePlan: () => void;
  sendPlanToParent: () => void;
}

const ManagedPlansCard: React.FC<ManagedPlansCardProps> = ({
  assignedUsers,
  assignPlan,
  completedUsers,
  deletePlan,
  duplicatePlan,
  editTranslations,
  plan,
  removePlan,
  renamePlan,
  sendPlanToParent,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { systemPlans } = useSystemPlans();
  const percentComplete = (completedUsers / assignedUsers) * 100;

  const isReadonlySubscribed = systemPlans.some(
    sp => sp.readonly && sp.id === plan.id,
  );

  const navigateToEditPlan = () => {
    navigate(routes.plans.managePlansEdit({ planId: plan.id }));
  };

  const planPermissions = Object.entries(plan.permissions ?? {}).reduce(
    (acc, [key, value]) => {
      if (value && key) {
        acc.add(key);
      }
      return acc;
    },
    new Set<string>(),
  );

  const renderPlanStatus = () => {
    if (!!assignedUsers && completedUsers === assignedUsers) {
      return (
        <PlanCardSubHeader className="success" data-testid="PlanTaskCount">
          {t('Generic.completedByAll')}
        </PlanCardSubHeader>
      );
    }

    const teamMemberStatus = assignedUsers
      ? `${completedUsers}/${assignedUsers} ${t(
          'Generic.teamMembersCompleted',
        )}`
      : t('Generic.noTeamMembersAssigned');

    return (
      <PlanCardSubHeader data-testid="PlanTaskCount">
        {plan.totalSteps === 0
          ? t('Generic.noContentInPlan')
          : teamMemberStatus}
      </PlanCardSubHeader>
    );
  };

  return (
    <PlanCard
      buttons={
        <PlanCardMenu
          assignedUsers={assignedUsers}
          assignPlan={assignPlan}
          deletePlan={deletePlan}
          duplicatePlan={duplicatePlan}
          editTranslations={editTranslations}
          isReadonly={isReadonlySubscribed}
          navigateToEditPlan={navigateToEditPlan}
          plan={plan}
          removePlan={removePlan}
          renamePlan={renamePlan}
          sendPlanToParent={sendPlanToParent}
          permissions={planPermissions}
        />
      }
      category={plan.category}
      dataTestId={'TeamPlanCard'}
      link={routes.plans.managePlan({ planId: plan.id })}
      progress={assignedUsers && plan.totalSteps ? percentComplete : null}
    >
      <PlanCardContent>
        <PlanCardLabel>
          {t(mapCategoryToAliasTranslation(plan.category))}
        </PlanCardLabel>
        <PlanCardTitle>{itemByLanguage(plan.name)}</PlanCardTitle>
        {renderPlanStatus()}
      </PlanCardContent>
      {!isReadonlySubscribed && <PlanCardDate date={plan.created} />}
    </PlanCard>
  );
};

export default ManagedPlansCard;
